"use strict";
/**
 * RBAC Role Model
 *
 * Hierarchy: creator > poweruser > member
 * - member:    Chat, run agents, use shared tools, browse, own sessions
 * - poweruser: + Own workspaces, create agents, own KB, connectors, projects, orchestrations
 * - creator:   + Global agents, system KB, admin, user management, Barnyard repo workspaces
 *
 * Canonical location: packages/shared/src/models/roles.ts. This file is the only source of
 * truth — orchestration/functions and orchestration/mcp-gateway consume it via the
 * `@cds/shared` file: dependency in their package.json. A duplicate `models/roles.ts`
 * anywhere else is a drift bug; a CI test in packages/shared/tests fails on a second copy.
 *
 * New authorization gates should call `requireGrant(request, Grant.X)` instead of
 * `requireRole` — see grants.ts and authz-package-review.md §B2. `requireRole` and
 * `hasRole` remain as building blocks for inline role checks that do not map cleanly
 * to a single grant (e.g. ownership overlays in barnskills and users).
 *
 * GLOBAL_AGENT_NAMES + SYSTEM_AGENT_USER_ID moved to orchestration/functions/src/models/
 * houseAgents.ts — they are an agents-API concern that belongs to the functions app, not the
 * shared role model. mcp-gateway does not consume them.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.CREATOR_ONLY_WORKSPACE_TARGETS = exports.ENTRA_APP_ROLES = exports.ROLE_LEVELS = exports.AppRole = void 0;
exports.hasRole = hasRole;
exports.resolveRole = resolveRole;
exports.toStoredRole = toStoredRole;
exports.capFromAppRoles = capFromAppRoles;
var AppRole;
(function (AppRole) {
    AppRole["Reviewer"] = "reviewer";
    AppRole["Member"] = "member";
    AppRole["PowerUser"] = "poweruser";
    AppRole["Creator"] = "creator";
})(AppRole || (exports.AppRole = AppRole = {}));
exports.ROLE_LEVELS = {
    [AppRole.Reviewer]: -1,
    [AppRole.Member]: 0,
    [AppRole.PowerUser]: 1,
    [AppRole.Creator]: 2,
};
/** Returns true if `userRole` meets or exceeds `requiredRole` in the hierarchy. */
function hasRole(userRole, requiredRole) {
    return exports.ROLE_LEVELS[userRole] >= exports.ROLE_LEVELS[requiredRole];
}
/** Safely convert a string to AppRole, defaulting to Member. */
function resolveRole(value) {
    if (value === 'creator')
        return AppRole.Creator;
    if (value === 'poweruser')
        return AppRole.PowerUser;
    if (value === 'reviewer')
        return AppRole.Reviewer;
    return AppRole.Member;
}
/**
 * The role to persist for a caller. Degrades `Reviewer` to `Member` rather than asserting it cannot
 * occur: `resolveRole('reviewer')` is total and a hand-edited Cosmos row could say it, and a cast
 * here would be a lie that only shows up as a schema violation at write time.
 */
function toStoredRole(role) {
    return role === AppRole.Reviewer ? AppRole.Member : role;
}
exports.ENTRA_APP_ROLES = {
    'Barnyard.Admin': AppRole.Creator,
    'Barnyard.User': AppRole.Member,
    'Barnyard.Reviewer': AppRole.Reviewer,
};
/**
 * The LOWEST role any `Barnyard.*` app role in a token maps to, or null when the token carries
 * none. It is a CEILING, never a promotion.
 *
 * ⚠️ This asymmetry is deliberate and is the whole design, so it is worth being blunt about what
 * it does and does not do. Authority in this system is stored in Cosmos, per user, and is what
 * every existing grant check reads. If an app role could raise that, then whoever can assign app
 * roles in Entra — a different set of people, in a different tenant (`app-fsdi`,
 * `42447d67-83b5-4c50-8105-12858cff3899`) — would gain a second, invisible path to Creator that no
 * audit of the user records would show. A ceiling adds no path: it can only take away.
 *
 * So `Barnyard.Admin` on a Member's token does NOT make them an admin. It says "this token is not
 * additionally restricted", which is the same thing as absence, and it exists because an Entra
 * registration that offers User and Reviewer but no Admin reads as an omission.
 * `Barnyard.Reviewer` is the one with teeth: it caps the token at read-only however senior the
 * human behind it is, which is what hands an auditor a credential that cannot change anything.
 *
 * A token carrying several app roles is capped at the lowest, for the same reason.
 *
 * No user has any of these assigned today — the roles do not exist on either registration yet —
 * so this returns null for every token currently minted and changes nothing until someone creates
 * and assigns them.
 */
function capFromAppRoles(roles) {
    if (!roles || roles.length === 0)
        return null;
    let cap = null;
    for (const claim of roles) {
        const mapped = exports.ENTRA_APP_ROLES[claim];
        if (mapped === undefined)
            continue;
        if (cap === null || exports.ROLE_LEVELS[mapped] < exports.ROLE_LEVELS[cap])
            cap = mapped;
    }
    return cap;
}
/** Workspace targets restricted to Creator only. */
exports.CREATOR_ONLY_WORKSPACE_TARGETS = new Set(['root']);
//# sourceMappingURL=roles.js.map